import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  switchMapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { SettingsService } from '~billing/services/settings.service';
import { slideInAndOutAnimation } from '~lmo/animations';
import { Frac, LmoFracListSummary } from '~lmo/models/frac.model';
import { GroupedMeshes, Mesh } from '~lmo/models/mesh.model';
import { Mine, MineWithClaws } from '~lmo/models/mine.model';
import { FracClusterPurchaseOrderRequest, PurchaseOrder, PurchaseOrderUpdate } from '~lmo/models/purchaseOrders.model';
import { milePluralMapping } from '~lmo/pluralMappings';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracMineService } from '~lmo/services/lmo-frac-mine.service';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { User } from '~models/user';
import { UserApiService } from '~services/api/user.api.service';
import { AuthService } from '~services/auth.service';
import { ErrorHandlingService } from '~services/error-handling.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';
import { LmoFcpoAddLoaderToNetworkComponent } from '../lmo-fcpo-add-loader-to-network/lmo-fcpo-add-loader-to-network.component';

const nullPoForm: EditPoFormValue = {
  costPerTon: null,
  dailyMaxTon: null,
  dailyMinTon: null,
  id: null,
  permittedFracIds: [],
  resetAllocationTime: null,
  subscribedSandiUsers: [],
  totalTonnage: null,
};

interface EditPoFormValue {
  costPerTon: number;
  dailyMaxTon: number;
  dailyMinTon: number;
  id: number;
  permittedFracIds: number[];
  resetAllocationTime: Date;
  subscribedSandiUsers: number[];
  totalTonnage: number;
}

@Component({
  animations: [slideInAndOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-fcpo-add',
  styleUrls: ['./lmo-frac-fcpo-add.component.scss'],
  templateUrl: './lmo-frac-fcpo-add.component.html',
})
export class LmoFracFcpoAddComponent implements OnInit, AfterViewInit, OnDestroy {
  public isGroupedAccountUser$: Observable<boolean>;
  @ViewChild('newPurchaseOrder', { static: true }) private newContractFormModal: TemplateRef<any>;
  @ViewChildren('newContractName') private newContractNameInput: QueryList<ElementRef>;
  @ViewChild('defaultLoadWeightWarning', { static: true }) private defaultLoadWeightWarningModal: TemplateRef<any>;
  public milePluralMapping = milePluralMapping;
  public floor = Math.floor;
  public meshPos$$: BehaviorSubject<PurchaseOrder[]> = new BehaviorSubject<PurchaseOrder[]>([]);
  public billableMileage$: Observable<number>;
  public fracMines$$ = new BehaviorSubject<MineWithClaws[]>([]);
  public currentFrac$: Observable<Frac>;
  public meshesInGroup$: Observable<GroupedMeshes[]>;
  public nonPoDataForm: FormGroup;
  public editPoForm: FormGroup;
  public newContractForm: FormControl;
  public submitting$$ = new BehaviorSubject(false);
  public fracList$: Observable<LmoFracListSummary[]>;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private newPoFormDialog: MatDialogRef<any>;
  public trackById = trackById;
  private lmoId: number;
  public isPOLineItemNumberMandatory = false;

  public filterUser: FormControl = new FormControl();
  private users$$ = new BehaviorSubject<User[]>([]);
  public filteredUsers$: Observable<User[]>;

  constructor(
    private fracService: LmoFracsService,
    private fb: FormBuilder,
    private fracMineService: LmoFracMineService,
    private fcpoService: LmoFracFcpoService,
    private modalService: LmoFracModalService,
    private matDialog: MatDialog,
    private errorService: ErrorHandlingService,
    private authService: AuthService,
    private usersService: UserService,
    private settingService: SettingsService,
    private userApiService: UserApiService,
  ) {
    this.userApiService.getWebUsers().subscribe((users) => {
      this.users$$.next(users);
    });

    this.filteredUsers$ = combineLatest([this.users$$, this.filterUser.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, searchFilter]) => {
        if (!searchFilter) {
          return users;
        }

        const searchFilterValue = searchFilter.toLowerCase();
        return users.filter((option) => option.name.toLowerCase().indexOf(searchFilterValue) === 0);
      }),
      takeUntil(this.destroy$),
    );
  }
  public ngOnInit() {
    this.currentFrac$ = this.fracService.currentFrac$.pipe(filter((frac) => !!frac));
    this.fracList$ = this.fracService.fracSummaries$;

    this.nonPoDataForm = this.fb.group({
      billableMileage: [null, [Validators.required, Validators.min(0)]],
      defaultLoadWeight: [null, [Validators.min(10000), Validators.max(42000)]],
      meshId: ['', [Validators.required]],
      mine: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      percentBasedAutoOrderingAllocation: [null, [Validators.min(0), Validators.max(100)]],
      permittedFracIds: [[]],
      poLineItemNumber: [null, []],
      purchaseOrder: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      subscribedSandiUsers: [[]],
    });
    this.lmoId = this.usersService.accountId();
    this.settingService.loadSettings(this.lmoId).subscribe((settings) => {
      this.isPOLineItemNumberMandatory = settings.isPOLineItemNumberMandatory;
      if (settings.isPOLineItemNumberMandatory) {
        this.nonPoDataForm.patchValue({
          poLineItemNumber: settings.defaultPOLineItemNumber,
        });
      }
    });

    this.setupMeshPurchaseOrders();
    this.setupEditPurchaseOrderForm();
    this.setupFormDisableAndEnableListeners();
    this.setupMeshes();
    this.newContractForm = this.fb.control(null, [Validators.required]);
    this.isGroupedAccountUser$ = this.authService.isGroupedAccountUser$;
  }

  public ngAfterViewInit() {
    this.newContractNameInput.changes.subscribe((input) => {
      if (input.length > 0) {
        input.first.nativeElement.focus();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    // Just in case
    this.destroy$.unsubscribe();
  }

  public async addFcpo() {
    if (
      this.editPoForm.invalid ||
      this.nonPoDataForm.invalid ||
      (this.nonPoDataForm.get('poLineItemNumber').value &&
        !this.nonPoDataForm.get('poLineItemNumber').value.trim() &&
        this.isPOLineItemNumberMandatory)
    ) {
      return;
    }

    const defaultLoadWeightValue = this.nonPoDataForm.get('defaultLoadWeight').value;
    // Less than half of the default load weight
    if (defaultLoadWeightValue !== null && defaultLoadWeightValue < 21000) {
      const confirmed = await this.matDialog
        .open(this.defaultLoadWeightWarningModal, {
          panelClass: ['w-full', 'lg:w-1/2'],
        })
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        return;
      }
    }

    this.submitting$$.next(true);
    const mineId = (this.nonPoDataForm.get('mine').value as Mine).id;
    const billableMileage = this.nonPoDataForm.get('billableMileage').value;
    const poLineItemNumber =
      this.nonPoDataForm.get('poLineItemNumber').value && this.nonPoDataForm.get('poLineItemNumber').value.trim()
        ? this.nonPoDataForm.get('poLineItemNumber').value.trim()
        : null;
    let defaultLoadWeight = this.nonPoDataForm.get('defaultLoadWeight').value;
    if (!defaultLoadWeight) {
      defaultLoadWeight = null;
    }

    this.fracMineService
      .setFracMineRoute$(mineId, billableMileage, defaultLoadWeight, false, null)
      .pipe(
        catchError(() => of(null)), // Ignore this if it fails
        switchMapTo(this.getNextPreferredOrder$()),
        map(
          (preferredOrder) =>
            ({
              fracClusterId: null,
              percentBasedAutoOrderingAllocation: this.nonPoDataForm.get('percentBasedAutoOrderingAllocation').value,
              poLineItemNumber: poLineItemNumber,
              preferredOrder,
              purchaseOrder: convertPoEditFormValueToPurchaseOrderUpdate(this.editPoForm.value as EditPoFormValue),
            } as FracClusterPurchaseOrderRequest),
        ),
        take(1),
        switchMap((fcpoRequest) => this.fcpoService.createFcpo$(fcpoRequest)),
      )
      .subscribe((successful) => {
        if (successful) {
          this.modalService.closeHomeModal();
        }
        this.submitting$$.next(false);
      });
  }

  public calculatePoRemaining(po: PurchaseOrder): number {
    const remaining = po.allocation - (po.pickedUp || 0);
    if (remaining < 0) {
      return 0;
    }
    return remaining;
  }

  public startNewPo() {
    this.newPoFormDialog = this.matDialog.open(this.newContractFormModal, {
      maxWidth: '500px;',
    });
  }

  public createPo() {
    const name = this.newContractForm.value;
    const mineId = this.nonPoDataForm.get('mine').value.id;
    const meshId = this.nonPoDataForm.get('meshId').value;
    if (!name || !mineId || !meshId) {
      return;
    }
    this.fcpoService.createPurchaseOrder$(name, mineId, meshId).subscribe(
      (po) => {
        if (po) {
          this.nonPoDataForm.get('purchaseOrder').setValue(po);
          if (this.newPoFormDialog) {
            this.newPoFormDialog.close();
            this.newPoFormDialog = null;
          }
        }
      },
      (error) => {
        this.errorService.showError(error);
      },
    );
  }

  public addNewLoader() {
    const addNewLoaderDialog = this.matDialog.open(LmoFcpoAddLoaderToNetworkComponent, {
      maxWidth: '500px;',
    });

    addNewLoaderDialog.afterClosed().subscribe((result: { id: number }) => {
      if (result) {
        this.getMinesForMesh(this.nonPoDataForm.get('meshId').value, result.id);
      }
    });
  }

  private setupEditPurchaseOrderForm() {
    this.editPoForm = this.fb.group({
      costPerTon: [null, [Validators.required, Validators.min(0)]],
      dailyMaxTon: [null, [Validators.required, Validators.min(0)]],
      dailyMinTon: [null, [Validators.required, Validators.min(0)]],
      id: [null, [Validators.required]],
      permittedFracIds: [null],
      resetAllocationTime: [null, [Validators.required]],
      subscribedSandiUsers: [null],
      totalTonnage: [null, [Validators.required, Validators.min(0)]],
    });
    this.editPoForm.disable();
  }

  private setupMeshPurchaseOrders() {
    combineLatest([
      this.fcpoService.purchaseOrders$,
      this.nonPoDataForm.get('meshId').valueChanges,
      this.nonPoDataForm.get('mine').valueChanges,
    ])
      .pipe(
        map(([purchaseOrders, meshId, mine]: [PurchaseOrder[], string, Mine]) => {
          if (meshId && mine && mine.id) {
            return purchaseOrders.filter((po) => po.meshId === +meshId && po.mineId === mine.id);
          }
          return [];
        }),
        tap((POs) => {
          POs.sort((a, b) => (a.allocation - (a.ordered || 0) < b.allocation - (b.ordered || 0) ? 1 : -1));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((POs) => {
        this.meshPos$$.next(POs);
        if (POs.length) {
          this.nonPoDataForm.get('purchaseOrder').enable();
        }
      });
  }

  private setupFormDisableAndEnableListeners() {
    this.nonPoDataForm
      .get('meshId')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((meshId) => {
        this.fracMines$$.next([]);
        this.nonPoDataForm.get('mine').setValue(null);
        if (meshId) {
          this.getMinesForMesh(meshId);
        } else {
          this.nonPoDataForm.get('mine').disable();
        }
      });

    this.nonPoDataForm
      .get('mine')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((mine: Mine) => {
        this.nonPoDataForm.get('purchaseOrder').setValue(null);
        if (mine) {
          this.billableMileage$ = this.fracMineService.getFracMineRoutes$(mine.id).pipe(
            map((results) => ({
              ...results,
              billableMileage: results.billableMileage === 0 ? 1 : results.billableMileage,
            })),
            tap((results) => {
              this.nonPoDataForm.get('billableMileage').setValue(results.billableMileage);
              this.nonPoDataForm.get('defaultLoadWeight').setValue(results.defaultLoadWeightPounds);
            }),
            map((results) => results.billableMileage),
          );
        } else {
          this.nonPoDataForm.get('purchaseOrder').disable();
        }
      });

    this.nonPoDataForm
      .get('purchaseOrder')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((po: PurchaseOrder) => {
        if (po) {
          const editPoForm = convertPurchaseOrderToEditPoForm(po);
          this.editPoForm.setValue(editPoForm);
          this.editPoForm.enable();
        } else {
          this.editPoForm.setValue(nullPoForm);
          this.editPoForm.disable();
        }
      });
  }

  private setupMeshes() {
    this.meshesInGroup$ = this.currentFrac$.pipe(
      map((frac) => {
        const uniqueMeshRecord = (frac.fracStorages || []).reduce((meshRecord, storage) => {
          meshRecord[storage.id] = {
            meshName: storage.mesh.type,
            meshes: [storage.mesh],
            storageId: storage.id,
          } as GroupedMeshes;
          return meshRecord;
        }, {});

        for (const fracStorage of frac.fracStorages) {
          for (const mesh of fracStorage.supplementalMeshes || []) {
            const supplementalMesh: Mesh = {
              id: mesh.meshId,
              isSupplemental: true,
              preferredOrder: 0,
              type: mesh.meshName,
            };
            uniqueMeshRecord[fracStorage.id].meshes.push(supplementalMesh);
          }
        }
        return Object.keys(uniqueMeshRecord).map((key) => uniqueMeshRecord[key]);
      }),
    );
  }

  private getMinesForMesh(meshId, selectedMineId = null) {
    this.fracMineService.getFracMinesForMesh$(meshId).subscribe((mines) => {
      this.fracMines$$.next(mines);
      this.nonPoDataForm.get('mine').enable();
      if (selectedMineId) {
        const selectedMine = mines.find((mine) => mine.id === selectedMineId);
        if (selectedMine) {
          this.nonPoDataForm.get('mine').setValue(selectedMine);
        }
      }
    });
  }

  private getNextPreferredOrder$(): Observable<number> {
    return this.fcpoService.currentFracFcpos$.pipe(
      map((fcpos) => {
        const existingPreferredOrders = fcpos
          .filter((fcpo) => fcpo.purchaseOrder.meshId === this.nonPoDataForm.get('meshId').value)
          .map((fcpo) => fcpo.preferredOrder);
        return Math.max(...existingPreferredOrders);
      }),
      take(1),
    );
  }
}

function convertPurchaseOrderToEditPoForm(po: PurchaseOrder): EditPoFormValue {
  const offsetAsTime = moment()
    .startOf('day')
    .add(po.resetAllocationTimeOffset, 'minutes');
  return {
    costPerTon: po.costPerTon,
    dailyMaxTon: po.maximumDailyAllocation ? po.maximumDailyAllocation / 2000 : 0,
    dailyMinTon: po.minimumDailyAllocation ? po.minimumDailyAllocation / 2000 : 0,
    id: po.id,
    permittedFracIds: po.permittedFracIds || [],
    resetAllocationTime: offsetAsTime.toDate(),
    subscribedSandiUsers: po.subscribedUsers ? po.subscribedUsers?.map((user) => user.id) : [],
    totalTonnage: po.allocation / 2000,
  };
}

function convertPoEditFormValueToPurchaseOrderUpdate(formValue: EditPoFormValue): PurchaseOrderUpdate {
  const pickedValue = moment(formValue.resetAllocationTime);
  const startOfDay = pickedValue.clone().startOf('day');
  const minutesOffSet = pickedValue.diff(startOfDay, 'minutes');
  return {
    allocation: formValue.totalTonnage * 2000,
    costPerTon: formValue.costPerTon,
    id: formValue.id,
    maximumDailyAllocation: (formValue.dailyMaxTon || 0) * 2000,
    minimumDailyAllocation: (formValue.dailyMinTon || 0) * 2000,
    permittedFracIds: formValue.permittedFracIds,
    resetAllocationTimeOffset: minutesOffSet,
    subscribedUserIds: formValue.subscribedSandiUsers,
  };
}
